import { AdminEmployeeRole } from '../interfaces/EmployeeInfoInterface';
import { Page } from '../interfaces/PagesInterface';
import { SiteConfig } from '../interfaces/ConfigInterface';
import type { StatusKey } from '../Pages/EmployeeTimesheetReviewConsole';

/**
 * Centralized Admin App permission logic (Approval Access WP8).
 *
 * The login response's role object carries the 21 Approval Access permission
 * booleans (mirrored on AdminEmployeeRole). `admin` is the super-admin bucket and
 * implicitly grants everything. Every capability gate in the app routes through
 * `can()` (and the stage helpers) — no component should read a role permission
 * boolean inline. Missing/undefined permissions (older API) read as `false`.
 */

/** The permission columns that gate capabilities (super-admin `admin` implies all). */
export type AdminPermission =
  | 'viewTimesheets'
  | 'approveTimesheets'
  | 'payrollApproveTimesheets'
  | 'unlockTimesheet'
  | 'unlockApprovedTimesheet'
  | 'viewTimeOffRequests'
  | 'approveDenyTimeOffRequests'
  | 'viewEmployees'
  | 'viewAccountCodes'
  | 'managePayPeriods'
  | 'manageLocations'
  | 'viewReports'
  | 'viewMiscTasks'
  | 'viewTimesheetComments'
  | 'viewModifiedDuty'
  | 'processPayroll'
  | 'importTasks'
  | 'exportAccounting'
  | 'exportPayroll'
  | 'viewAllEmployees'
  | 'viewProjectWork'
  | 'viewWorkOrders'
  | 'manageWorkOrders'
  | 'manageWorkOrderRates'
  | 'manageCompanyPhones'
  // Existing fine-grained flags still honored through can() for uniformity.
  | 'editEmployeeRole'
  | 'viewGpsExceptions'
  | 'viewSelfieExceptions'
  | 'activateInactivateEmployees'
  // Payroll Timesheet Modifications (TAS-59).
  | 'editTimesheetEntries'
  | 'submitTimesheetForEmployee'
  | 'editApprovedTimesheet'
  // NPM reds two-level approval (TAS-358, D17). `approveMiscTasks` lets a
  // resolved level-1 / level-2 approver act on the reds routed to them;
  // `overrideMiscTaskApproval` is the oversight escape hatch that bypasses the
  // routing entirely (force-approve, deny at any stage, correct the Vista text).
  | 'approveMiscTasks'
  | 'overrideMiscTaskApproval'
  // Change Supervisor: move a single timesheet to a different supervisor for that
  // pay period only. Scope-bound at the API — holding it is not sufficient if the
  // timesheet's employee is outside the caller's scope.
  | 'reassignTimesheetSupervisor'
  // Time off corrections / on-behalf submission. Split out of approveDenyTimeOffRequests,
  // which most roles already hold — these two are granted by hand, never back-filled.
  | 'editApprovedTimeOffRequests'
  | 'submitTimeOffForEmployee'
  // Push a reminder to every unsubmitted employee for the pay period (company-wide), and
  // see + edit the weekly safety message. Both were super-admin-only before; the company
  // config gates (showPushNotificationButton / showSafetyMessagePage) still apply.
  | 'sendTimesheetReminders'
  | 'manageSafetyMessages'
  // Kiosk Devices page (TAS-585): add tablets, issue enrollment codes, deactivate.
  | 'manageKioskDevices'
  // NPM LA Sick page (People › LA Sick): who worked Los Angeles jobs this year and whether
  // Vista has them on CA LA SICK. Read-only; the showLaSickPage company config also applies.
  | 'viewLASick'
  // Employee Roles page create/edit (TAS-667). /admin/roles POST/PUT require it.
  | 'manageEmployeeRoles'
  // Legacy misc task cost-code/description editor, surfaced on the V2 review console.
  | 'editMiscTaskAccountCodeAndDescription';

/**
 * Capability check. Returns true when the role is a super admin (`admin`) or the
 * specific permission column is set. Tolerant of a missing role / undefined column.
 */
export function can(role: AdminEmployeeRole | null | undefined, permission: AdminPermission): boolean {
  if (!role) return false;
  if (role.admin) return true;
  return role[permission] === true;
}

/**
 * Work Orders / Change Orders / Builder Contacts visibility. Mirrors the API's
 * `requireWorkOrderPermission`: reads need `viewWorkOrders`, and `manageWorkOrders`
 * implies read. The v2 sidebar used to gate on the coarse `admin || payroll`
 * buckets instead, so a role granted View Work Orders (but not Payroll) never saw
 * the page, and a Payroll role without it saw a page that 403'd (TAS-555).
 */
export function canViewWorkOrders(role: AdminEmployeeRole | null | undefined): boolean {
  return can(role, 'viewWorkOrders') || can(role, 'manageWorkOrders');
}

/** True only for the super-admin bucket (surfaces with no decomposed permission). */
export function isSuperAdmin(role: AdminEmployeeRole | null | undefined): boolean {
  return !!role && role.admin === true;
}

/**
 * Which unlock permission the current bucket stage requires (spec §4.4):
 *   pre-payroll (Submitted / Manager Pending) → unlockTimesheet
 *   payroll     (Supervisor Approved / Payroll Approved) → unlockApprovedTimesheet
 * Super admin always allowed. `unsubmitted`/null are not unlockable.
 */
export function canUnlockStage(
  role: AdminEmployeeRole | null | undefined,
  status: StatusKey | null | undefined
): boolean {
  if (!status || status === 'unsubmitted') return false;
  if (isSuperAdmin(role)) return true;
  if (status === 'submitted' || status === 'managerPending') return can(role, 'unlockTimesheet');
  // supervisorApproved (Payroll Pending) | payrollApproved (final)
  return can(role, 'unlockApprovedTimesheet');
}

/**
 * Which approve permission the current bucket stage requires (spec §4.4 / §15.3.15):
 *   1st approval (Manager Pending) → approveTimesheets (v1 semantics: any authorized
 *     admin may satisfy a manager / approval-group slot)
 *   manager/supervisor stage (Submitted) → approveTimesheets
 *   Payroll Pending (Supervisor Approved) → payrollApproveTimesheets
 * Super admin always allowed. Unsubmitted / Payroll Approved never advance.
 */
export function canApproveStage(
  role: AdminEmployeeRole | null | undefined,
  status: StatusKey | null | undefined
): boolean {
  if (isSuperAdmin(role)) return true;
  if (status === 'submitted' || status === 'managerPending') return can(role, 'approveTimesheets');
  if (status === 'supervisorApproved') return can(role, 'payrollApproveTimesheets');
  return false;
}

/**
 * May create / edit / delete an employee's unsubmitted time entries and tasks as
 * payroll (Payroll Timesheet Modifications, Milestone 2). Super admin always allowed.
 */
export function canEditEntries(role: AdminEmployeeRole | null | undefined): boolean {
  return can(role, 'editTimesheetEntries');
}

/**
 * May submit an unsubmitted timesheet on an employee's behalf — recorded as a
 * PAYROLL submission (Payroll Timesheet Modifications, Milestone 1).
 */
export function canSubmitForEmployee(role: AdminEmployeeRole | null | undefined): boolean {
  return can(role, 'submitTimesheetForEmployee');
}

/**
 * May modify a payroll-approved timesheet in place (Payroll Timesheet Modifications,
 * Milestone 3 — permission plumbed now, feature lands later).
 */
export function canEditApproved(role: AdminEmployeeRole | null | undefined): boolean {
  return can(role, 'editApprovedTimesheet');
}

/**
 * A "limited" user has none of the coarse bucket roles, so the bucket-gated Home
 * (Dashboard) nav item is hidden for them — they must be routed to whatever page
 * their permissions unlock. (Spec finding §13.8: a permissions-only role such as
 * viewTimesheets-only was previously misclassified and misrouted to a hidden Home.)
 */
export function isLimitedUser(role: AdminEmployeeRole | null | undefined): boolean {
  if (!role) return true;
  return !role.admin && !role.payroll && !role.supervisor && !role.hr;
}

/**
 * Landing page for a limited (bucketless) user, derived from the new permissions
 * first (§13.8): viewTimesheets → Timesheet Review, viewTimeOffRequests → Time Off
 * Approvals, viewEmployees → Employee View, viewProjectWork → Project Work, then the
 * existing fine-grained fallbacks. Dashboard is the final fallback.
 */
export function getDefaultPageForLimitedUser(role: AdminEmployeeRole | null | undefined, config: SiteConfig): Page {
  // No role (employee-only login, TAS-667) = no permissions.
  if (!role) return Page.Dashboard;
  if (config.showTimesheetReviewPage && can(role, 'viewTimesheets')) return Page.TimesheetReview;
  if (config.showTimeOffApprovalPage && can(role, 'viewTimeOffRequests')) return Page.TimeOffRequestApproval;
  if (can(role, 'viewEmployees')) return Page.EmployeeView;
  if (config.showProjectWorkPage && can(role, 'viewProjectWork')) return Page.ProjectWork;
  if (config.showCrewWorkPage && can(role, 'viewProjectWork')) return Page.CrewWork;
  if (config.showProjectsPage && role.viewProjectsPage) return Page.Projects;
  if (can(role, 'importTasks') && config.allow2020PieceImport) return Page.PieceImport;
  if (can(role, 'importTasks') && config.allowRiceServicesPieceImport) return Page.RiceServicesPieceImport;
  if (can(role, 'importTasks') && config.allowUpfPieceImport) return Page.UPFPieceBudgetImport;
  if (role.viewAllProjectsMap) return Page.ProjectMap;
  return Page.Dashboard;
}
